'use client'

import { useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'

import { useOnboardingFlow } from '@common/apps'
import { getUserInvitations } from '@common/modules/user'
import { useUser as useUserData, useUserStore } from '@common/modules/user/stores'

import { APP_INITIALIZATION_LOADER_MESSAGE } from '../const/app-initialization-loader-message.const'
import { getSessionToken, isProduction } from '../helpers'
import { useLayoutStore } from '../stores/layout.store'
import { isAcceptInvitationRoute } from '../utils/isAcceptInvitationRoute'
import { logZustandStores } from '../utils/logZustandStores'
import { resolveWorkspaceProjectAndSyncLastUsed } from '../utils/resolveWorkspaceProjectAndSyncLastUsed'
import { runInitialAppDataParallelBatch } from '../utils/runInitialAppDataParallelBatch'

import { useAuthUser } from './useAuthUser'
import { useUserRegionalData } from './useUserRegionalData'

export function useAppInitialization(isEmailVerified: boolean) {
  const pathname = usePathname()
  const { user: auth0User, isLoading: auth0Loading } = useAuthUser()
  const startLoader = useLayoutStore((s) => s.startLoader)
  const clearFullScreenLoader = useLayoutStore((s) => s.clearFullScreenLoader)

  const { getCurrentUser } = useUserData()
  const { initializeUserRegionalData } = useUserRegionalData()

  const { isOnboarding, ensureOnboardingComplete } = useOnboardingFlow()

  const [isInitializing, setIsInitializing] = useState(true)
  const [hasInitialized, setHasInitialized] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const initStarted = useRef(false)
  const onboardingStarted = useRef(false)
  const isVerified = auth0User?.email_verified === true || isEmailVerified
  const isAcceptInvitation = isAcceptInvitationRoute(pathname)
  const canInitializeAppData = isVerified || isAcceptInvitation

  useEffect(() => {
    if (auth0Loading || !auth0User || !canInitializeAppData || initStarted.current) return
    initStarted.current = true

    const run = async () => {
      startLoader(APP_INITIALIZATION_LOADER_MESSAGE)
      setIsInitializing(true)
      setHasInitialized(true)
      setError(null)
      try {
        await getCurrentUser()
        await getSessionToken()

        const { workspace } = await runInitialAppDataParallelBatch(initializeUserRegionalData)

        if (workspace) {
          await resolveWorkspaceProjectAndSyncLastUsed(workspace)
        }

        useUserStore.getState().setInvitations(await getUserInvitations())
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to initialize app data')
        console.error('[useAppInitialization]', err)
      } finally {
        clearFullScreenLoader()
        setIsInitializing(false)
      }
    }
    void run()
  }, [
    auth0Loading,
    auth0User,
    canInitializeAppData,
    getCurrentUser,
    initializeUserRegionalData,
    startLoader,
    clearFullScreenLoader,
  ])

  useEffect(() => {
    if (auth0Loading) return

    if (!auth0User) {
      clearFullScreenLoader()
      setIsInitializing(false)
      return
    }

    if (isVerified || isAcceptInvitation) return

    setHasInitialized(true)
    clearFullScreenLoader()
    setIsInitializing(false)
  }, [auth0Loading, auth0User, isVerified, isAcceptInvitation, clearFullScreenLoader])

  useEffect(() => {
    if (auth0Loading || !auth0User || !isVerified || isInitializing || error) return
    if (isAcceptInvitation) return
    if (onboardingStarted.current) return
    onboardingStarted.current = true
    ensureOnboardingComplete()
  }, [auth0User, auth0Loading, isVerified, isInitializing, error, ensureOnboardingComplete, isAcceptInvitation])

  useEffect(() => {
    const ready = !auth0Loading && !!auth0User && isVerified && !isInitializing

    if (!ready || isProduction()) return

    logZustandStores()
  }, [auth0User, auth0Loading, isVerified, isInitializing])

  const isReady = !auth0Loading && !!auth0User && hasInitialized && !isInitializing && isVerified

  return {
    isReady,
    isInitializing,
    hasInitialized,
    initializationError: error,
    isOnboarding,
  }
}
